import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send, Image as ImageIcon, ChevronDown, ChevronUp, Loader2,
  Settings as SettingsIcon, Box, MessageSquare, Menu, X,
  FileCode, Box as BoxIcon, Copy, Check, ArrowLeft, Trash2,
  AlertTriangle, ShieldAlert, RefreshCw, Briefcase, Bot, User, FileQuestion, ArrowDown
} from 'lucide-react';

// --- FIREBASE IMPORTS ---
import { ref, onValue, query, limitToLast, get } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import { auth, realtimeDb as database } from './firebase';

// --- MOCK ASSETS ---
import { projectAssets as mockAssets } from './mockData';
import { Button } from './components/Button';

// --- CONFIGURATION ---
const PROXY_URL = "https://everydaycats-ai-plugin-server.hf.space";

// --- HELPER: BASE64 ---
const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
};

export default function ProjectWorkspace() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [idToken, setIdToken] = useState(null);

  // --- UI STATE ---
  const [activeTab, setActiveTab] = useState('chat');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);

  // --- DATA STATE ---
  const [projectInfo, setProjectInfo] = useState({ title: "Loading...", userId: "" });
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processLogs, setProcessLogs] = useState([]);

  // --- PAGINATION & NAVIGATION STATE ---
  const [historyLimit, setHistoryLimit] = useState(30); // Start with 30
  const [showScrollBottom, setShowScrollBottom] = useState(false); // Toggle for floating btn

  // --- ERROR STATE ---
  const [creditError, setCreditError] = useState(null);

  // --- STATUS STATES ---
  const [notFound, setNotFound] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // --- KEY STATE ---
  const [generatedKey, setGeneratedKey] = useState(null);
  const [isRolling, setIsRolling] = useState(false);

  // --- INPUT STATE ---
  const [input, setInput] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null); // Ref for the scrollable container
  const fileInputRef = useRef(null);

  // 1. AUTH
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const token = await user.getIdToken();
        setIdToken(token);
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // 2. INFO
  useEffect(() => {
    const infoRef = ref(database, `projects/${id}/info`);
    get(infoRef).then((snapshot) => {
      if (snapshot.exists()) {
        setProjectInfo(snapshot.val());
      } else {
        setNotFound(true);
      }
    }).catch(() => setNotFound(true));
  }, [id]);

  // 3. CHAT & PAGINATION
  useEffect(() => {
    if (notFound) return;

    const historyRef = ref(database, `projects/${id}/state/workerHistory`);
    // DYNAMIC LIMIT: Updates based on scroll
    const recentHistoryQuery = query(historyRef, limitToLast(historyLimit));

    const unsubscribe = onValue(recentHistoryQuery, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const historyArray = Array.isArray(data) ? data : Object.values(data);

        const formattedMessages = historyArray.map((msg, index) => ({
            id: index,
            role: msg.role === 'model' ? 'ai' : 'user',
            content: msg.parts?.[0]?.text || "",
            image: null
        }));

        setMessages(formattedMessages);

        const lastMsg = formattedMessages[formattedMessages.length - 1];
        if (lastMsg && lastMsg.role === 'ai') {
            setIsProcessing(false);
            setProcessLogs([]);
        }
      } else {
        setMessages([]);
      }
    });

    const statusRef = ref(database, `projects/${id}/info/status`);
    const statusUnsub = onValue(statusRef, (snap) => {
        if(snap.val() === 'working') {
             setIsProcessing(true);
             setProcessLogs(prev => prev.length === 0 ? ["AI Agent is thinking..."] : prev);
        }
    });

    return () => { unsubscribe(); statusUnsub(); };
  }, [id, notFound, historyLimit]); // Re-run when historyLimit changes

  // 4. AUTO SCROLL (New Messages)
  useEffect(() => {
    // Only scroll to bottom automatically if we are already near the bottom OR if it's the first load
    // This prevents jumping when loading old messages (pagination)
    if (!showScrollBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isProcessing, processLogs]);

  // 5. FIX SCROLL ON TAB RETURN
  useLayoutEffect(() => {
      if (activeTab === 'chat') {
          // Force scroll to bottom immediately when switching back to chat
          messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }
  }, [activeTab]);

  // --- SCROLL HANDLER (Pagination + Nav Button) ---
  const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      
      // A. Show "Jump to Bottom" button if user scrolls up > 300px
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;
      setShowScrollBottom(distanceToBottom > 300);

      // B. Infinite Scroll (Load older messages)
      if (scrollTop === 0 && messages.length >= historyLimit) {
          // If at top and we might have more history, load 20 more
          setHistoryLimit(prev => prev + 20);
      }
  };

  const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setShowScrollBottom(false);
  };

  // --- ACTIONS ---

  const generatePluginKey = async () => {
    if (!idToken) return;
    setIsRolling(true);
    try {
        const response = await fetch(`${PROXY_URL}/key`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ projectId: id })
        });
        const data = await response.json();
        if (data.key) setGeneratedKey(data.key);
    } catch (e) { console.error(e); }
    finally { setIsRolling(false); }
  };

  useEffect(() => {
    if (activeTab === 'settings' && !generatedKey && idToken && !notFound) {
        generatePluginKey();
    }
  }, [activeTab, idToken, notFound]);

  const handleSend = async () => {
    if((!input.trim() && !selectedFile) || isProcessing || !currentUser) return;

    setCreditError(null); 

    const tempMsg = { id: Date.now(), role: 'user', content: input, image: previewUrl };
    setMessages(prev => [...prev, tempMsg]);

    const promptText = input;
    const fileToSend = selectedFile;

    setInput("");
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsProcessing(true);
    setProcessLogs(["Sending request..."]);
    
    // Auto scroll on send
    setTimeout(scrollToBottom, 100);

    try {
        let imagesPayload = [];
        if (fileToSend) {
            const base64 = await convertFileToBase64(fileToSend);
            imagesPayload.push(base64);
        }

        const response = await fetch(`${PROXY_URL}/feedback2`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
                projectId: id,
                prompt: promptText,
                images: imagesPayload,
                logContext: null,
                hierarchyContext: null,
                scriptContext: null
            })
        });

        const data = await response.json();

        if (data.insufficient) {
            setCreditError(data.error);
            setIsProcessing(false);
            return;
        }

        if (!data.success) setIsProcessing(false);
    } catch (err) {
        setIsProcessing(false);
    }
  };

  const handleConfirmNullify = async () => {
    if (!idToken) return;
    try {
        await fetch(`${PROXY_URL}/nullify`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ projectId: id })
        });
        setShowSessionModal(false);
        generatePluginKey();
    } catch(e) { console.error(e); }
  };

  const handleDeleteProject = async () => {
     if (!idToken) return;
     setIsDeleting(true);

     try {
         const res = await fetch(`${PROXY_URL}/project/delete`, {
             method: 'POST',
             headers: {
                 'Content-Type': 'application/json',
                 'Authorization': `Bearer ${idToken}`
             },
             body: JSON.stringify({ projectId: id })
         });
         const data = await res.json();
         if(data.success) {
             navigate('/console');
         } else {
             alert("Delete Failed: " + (data.error || "Unknown Error"));
             setIsDeleting(false);
         }
     } catch (e) {
         console.error(e);
         alert("Network Error during deletion.");
         setIsDeleting(false);
     }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if(file) {
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if(fileInputRef.current) fileInputRef.current.value = "";
  };

  if (notFound) {
      return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-300 p-4">
            <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl shadow-2xl text-center max-w-md w-full">
                <div className="mx-auto w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-6 text-slate-500">
                    <FileQuestion size={32} />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Project Not Found</h1>
                <p className="text-slate-400 mb-8">
                    This project ID does not exist or may have been deleted.
                </p>
                <button
                    onClick={() => navigate('/console')}
                    className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl transition-all"
                >
                    <ArrowLeft size={18} /> Return to Console
                </button>
            </div>
        </div>
      );
  }

  // --- MAIN RENDER ---
  return (
    <div className="fixed inset-0 bg-slate-950 text-slate-200 flex overflow-hidden font-sans">

      {/* Sidebar Desktop */}
      <aside className="hidden md:flex flex-col w-64 border-r border-slate-800 bg-slate-950 shrink-0 z-30 justify-between">
        <div>
            <div className="h-16 flex items-center gap-3 px-6 border-b border-slate-800/50">
                <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg object-contain bg-slate-900 border border-slate-800" />
                <span className="font-bold text-lg text-white tracking-tight">BloxBuddy</span>
            </div>
            <div className="flex flex-col gap-2 p-4">
                <NavIcon label="Chat" icon={<MessageSquare size={20} />} active={activeTab === 'chat'} onClick={() => setActiveTab('chat')} />
                <NavIcon label="Settings" icon={<SettingsIcon size={20} />} active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} />
            </div>
        </div>
        <div className="p-4 border-t border-slate-800 bg-slate-900/30">
            <button onClick={() => navigate('/console')} className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all group">
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium">Back to Console</span>
            </button>
        </div>
      </aside>

      {/* Sidebar Mobile */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 md:hidden" onClick={() => setIsSidebarOpen(false)} />}
      <div className={`fixed inset-y-0 left-0 z-[60] w-72 bg-slate-900 border-r border-slate-800 transform transition-transform duration-300 flex flex-col justify-between ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:hidden`}>
        <div>
            <div className="flex items-center justify-between p-4 border-b border-slate-800 mb-4 h-16">
                <div className="flex items-center gap-3">
                    <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
                    <span className="font-bold text-white text-xl">BloxBuddy</span>
                </div>
                <button onClick={() => setIsSidebarOpen(false)} className="text-slate-400 p-2"><X size={24} /></button>
            </div>
            <div className="flex flex-col gap-2 px-4">
                <NavIcon label="Chat" icon={<MessageSquare size={24} />} active={activeTab === 'chat'} onClick={() => { setActiveTab('chat'); setIsSidebarOpen(false); }} />
                <NavIcon label="Settings" icon={<SettingsIcon size={24} />} active={activeTab === 'settings'} onClick={() => { setActiveTab('settings'); setIsSidebarOpen(false); }} />
            </div>
        </div>
        <div className="p-4 border-t border-slate-800">
             <button onClick={() => navigate('/console')} className="flex items-center gap-4 px-4 py-3 w-full rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-all"><ArrowLeft size={24} /><span className="font-medium text-lg">Back to Console</span></button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 h-full relative">
        <header className="h-16 shrink-0 border-b border-slate-800 bg-slate-900 flex items-center px-4 justify-between z-20">
            <div className="flex items-center gap-3">
                <button onClick={() => setIsSidebarOpen(true)} className="md:hidden p-2 text-slate-400 hover:text-white"><Menu size={24} /></button>
                <span className="font-bold text-white text-lg tracking-tight truncate">{projectInfo.title}</span>
            </div>
            <div className="text-xs text-slate-500 font-mono hidden sm:block">ID: {id}</div>
        </header>

        <main className="flex-1 flex flex-col min-h-0 overflow-hidden relative bg-slate-950">
            {activeTab === 'chat' && (
                <div className="flex flex-col h-full w-full relative">
                    {/* Chat Container */}
                    <div 
                        ref={chatContainerRef}
                        onScroll={handleScroll}
                        className="flex-1 overflow-y-auto p-4 md:p-8 space-y-6 scroll-smooth min-h-0"
                    >
                        {messages.length === 0 && !isProcessing && (
                             <div className="text-center text-slate-600 mt-10"><p>Welcome to <strong>{projectInfo.title}</strong></p></div>
                        )}

                        {messages.map((msg) => {
                            const isUser = msg.role === 'user';
                            const isPM = !isUser && (msg.content.includes('[PM RESPONSE]') || msg.content.includes('PM RESPONSE:'));
                            let bubbleClass = "bg-slate-900 border border-slate-800 text-slate-200 rounded-bl-none";
                            if (isUser) {
                                bubbleClass = "bg-[#172554] border border-blue-900/50 text-slate-200 rounded-br-none";
                            } else if (isPM) {
                                bubbleClass = "bg-[#0a0b14] border border-indigo-500/20 text-indigo-100 rounded-bl-none shadow-lg shadow-indigo-900/10";
                            }

                            return (
                                <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[95%] md:max-w-[85%] p-4 rounded-xl text-sm md:text-base shadow-sm ${bubbleClass}`}>
                                        {msg.image && <img src={msg.image} alt="attachment" className="mb-3 rounded-lg border border-white/10 w-72 h-48 object-cover bg-slate-950 block" />}
                                        <MessageRenderer content={msg.content} />
                                    </div>
                                </div>
                            );
                        })}

                        {isProcessing && ( <CollapsibleProcess logs={processLogs} /> )}
                        
                        {/* Invisible div for auto-scrolling */}
                        <div ref={messagesEndRef} className="h-1" />
                    </div>

                    {/* Quick Navigation Button (Floating) */}
                    {showScrollBottom && (
                        <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-30 animate-in fade-in zoom-in duration-200">
                             <button 
                                onClick={scrollToBottom}
                                className="bg-slate-800/90 hover:bg-slate-700 text-blue-400 border border-slate-700 shadow-lg backdrop-blur-sm p-2 rounded-full flex items-center gap-2 px-4 transition-all hover:scale-105"
                             >
                                <ArrowDown size={16} />
                                <span className="text-xs font-semibold">Scroll to Bottom</span>
                             </button>
                        </div>
                    )}

                    <div className="shrink-0 bg-slate-900 border-t border-slate-800 p-4 z-20 w-full relative">
                        {creditError && (
                            <div className="mb-3 p-3 bg-red-950/40 border border-red-500/30 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2">
                                <ShieldAlert size={18} className="text-red-500 mt-0.5 shrink-0" />
                                <div className="flex-1 text-sm text-red-200">
                                    <span className="font-bold text-red-400 block mb-0.5">Payment Required</span>
                                    {creditError}
                                </div>
                                <button onClick={() => setCreditError(null)} className="text-slate-500 hover:text-white transition-colors">
                                    <X size={16} />
                                </button>
                            </div>
                        )}

                        {previewUrl && (
                            <div className="absolute bottom-full left-4 mb-2 bg-slate-800 border border-slate-700 p-2 rounded-xl shadow-xl flex flex-col w-40 animate-in slide-in-from-bottom-2">
                                <div className="relative group"><img src={previewUrl} className="h-24 w-full rounded-lg object-cover" alt="Preview" /><button onClick={clearFile} disabled={isProcessing} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:scale-110 transition-transform"><X size={12} /></button></div>
                            </div>
                        )}
                        <div className={`max-w-4xl mx-auto flex items-end gap-3 bg-slate-950 p-2 rounded-xl border transition-all duration-300 ${isProcessing ? 'border-slate-800 opacity-60 cursor-wait' : 'border-slate-800 shadow-inner focus-within:border-blue-500/50'}`}>
                            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                            <button onClick={() => fileInputRef.current?.click()} disabled={isProcessing} className="p-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:hover:bg-transparent transition-all"><ImageIcon size={20} /></button>
                            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()} disabled={isProcessing} placeholder={isProcessing ? "AI is working..." : "Describe what to build..."} className="flex-1 bg-transparent border-none outline-none text-white resize-none max-h-32 py-3 placeholder:text-slate-600 disabled:cursor-not-allowed" rows={1} />
                            <Button onClick={handleSend} disabled={(!input.trim() && !selectedFile) || isProcessing} className="h-10 w-10 p-0 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-500">{isProcessing ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}</Button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'assets' && (
                <div className="flex-1 overflow-y-auto p-4 md:p-8 min-h-0">
                     <h2 className="text-2xl font-bold mb-6 text-white">Project Assets</h2>
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {mockAssets.map(asset => (
                            <div key={asset.id} className="bg-slate-900 border border-slate-800 p-4 rounded-xl flex items-center gap-4 hover:border-blue-500/30 transition-colors cursor-pointer group">
                                <div className={`p-3 rounded-lg ${asset.type === 'script' ? 'bg-blue-900/20 text-blue-400' : 'bg-orange-900/20 text-orange-400'}`}>{asset.type === 'script' ? <FileCode size={24} /> : <BoxIcon size={24} />}</div>
                                <div><div className="font-semibold text-white group-hover:text-blue-300 transition-colors">{asset.name}</div><div className="text-xs text-slate-500 uppercase">{asset.type} • {asset.size}</div></div>
                            </div>
                        ))}
                     </div>
                </div>
            )}

            {activeTab === 'settings' && (
                <div className="flex-1 overflow-y-auto p-4 md:p-8 min-h-0">
                    <div className="max-w-3xl mx-auto space-y-6 pb-12">
                        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl transition-all">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-bold text-white">Plugin Auth Key</h3>
                                <button onClick={generatePluginKey} disabled={isRolling} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                                    <RefreshCw size={12} className={isRolling ? "animate-spin" : ""} /> Regenerate
                                </button>
                            </div>
                            <p className="text-sm text-slate-400 mb-4">Paste this key into the Roblox Studio plugin to connect this project.</p>
                            <CredentialCopyBox value={generatedKey || "Generating..."} isRolling={isRolling || !generatedKey} />
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl">
                            <div className="flex items-center gap-2 mb-2">
                                <ShieldAlert className="text-amber-500" size={20} />
                                <h3 className="text-lg font-bold text-white">Session Management</h3>
                            </div>
                            <p className="text-sm text-slate-400 mb-6">
                                Forcefully disconnect all active Roblox Studio sessions if you suspect a key leak.
                            </p>
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setShowSessionModal(true)}
                                    className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 border border-amber-500/50 px-4 py-2 rounded-lg transition-all flex items-center gap-2 text-sm font-semibold"
                                >
                                    <ShieldAlert size={16} /> Nullify All Sessions
                                </button>
                            </div>
                        </div>

                        <div className="bg-red-950/10 border border-red-900/30 rounded-xl p-6">
                            <h3 className="text-lg font-bold text-red-500 flex items-center gap-2 mb-4">
                                <AlertTriangle size={20} /> Danger Zone
                            </h3>
                            <div className="flex justify-end">
                                <button onClick={() => setShowDeleteModal(true)} className="bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white border border-red-600/50 px-4 py-2 rounded-lg transition-all flex items-center gap-2 text-sm font-semibold">
                                    <Trash2 size={16} /> Delete Project
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </main>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl max-w-md w-full shadow-2xl">
                <div className="flex items-center gap-4 mb-4 text-red-500"><div className="p-3 bg-red-500/10 rounded-full"><AlertTriangle size={32} /></div><h3 className="text-xl font-bold text-white">Delete Project?</h3></div>
                <p className="text-slate-400 mb-8 leading-relaxed">Are you sure you want to delete <span className="text-white font-semibold">{projectInfo.title}</span>?</p>
                <div className="flex items-center justify-end gap-3">
                    <button onClick={() => setShowDeleteModal(false)} disabled={isDeleting} className="px-5 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors font-medium">Cancel</button>

                    <button
                        onClick={handleDeleteProject}
                        disabled={isDeleting}
                        className="px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-900/20 transition-all font-bold flex items-center gap-2 disabled:bg-red-900 disabled:opacity-50"
                    >
                        {isDeleting ? <Loader2 size={18} className="animate-spin" /> : null}
                        {isDeleting ? "Deleting..." : "Yes, Delete It"}
                    </button>
                </div>
            </div>
        </div>
      )}
      {showSessionModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl max-w-md w-full shadow-2xl">
                <div className="flex items-center gap-4 mb-4 text-amber-500"><div className="p-3 bg-amber-500/10 rounded-full"><ShieldAlert size={32} /></div><h3 className="text-xl font-bold text-white">Nullify Sessions?</h3></div>
                <p className="text-slate-400 mb-8 leading-relaxed">This will disconnect the plugin.</p>
                <div className="flex items-center justify-end gap-3"><button onClick={() => setShowSessionModal(false)} className="px-5 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors font-medium">Cancel</button><button onClick={handleConfirmNullify} className="px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-900/20 transition-all font-bold">Confirm & Reset</button></div>
            </div>
        </div>
      )}
    </div>
  );
}

// --- SUB COMPONENTS ---

function CollapsibleCode({ code, language = "Script" }) {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="my-3 rounded-lg overflow-hidden border border-slate-700 bg-[#0d1117] shadow-sm">
            <div
                className="flex items-center justify-between px-4 py-2 bg-slate-800/50 cursor-pointer hover:bg-slate-800 transition-colors select-none"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-2 text-xs font-mono font-semibold text-blue-400"><FileCode size={14} /><span>{language}</span></div>
                <div className="flex items-center gap-3">
                     <button onClick={handleCopy} className="text-slate-500 hover:text-white transition-colors">{copied ? <Check size={14} className="text-green-400"/> : <Copy size={14} />}</button>
                    <div className="flex items-center gap-1 text-slate-500 text-xs"><span>{expanded ? 'Collapse' : 'Expand'}</span>{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</div>
                </div>
            </div>
            {expanded && <div className="p-4 overflow-x-auto bg-[#0d1117]"><pre className="text-xs font-mono text-slate-300 whitespace-pre leading-relaxed">{code}</pre></div>}
        </div>
    );
}

function FormatInline({ text }) {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return <>{parts.map((part, i) => part.startsWith('**') && part.endsWith('**') ? <strong key={i} className="font-bold text-blue-200">{part.slice(2, -2)}</strong> : part)}</>;
}

function CollapsiblePM({ content }) {
    const [expanded, setExpanded] = useState(false);

    const cleanContent = content.replace(/```[\s\S]*?```/g, "").trim();
    const sentences = cleanContent.match(/[^.!?]+[.!?]+[\])'"]*/g) || [cleanContent];
    const previewText = sentences.slice(0, 2).join(' ').trim();
    const hasMore = sentences.length > 2 || content.length > 300;

    return (
        <div className="transition-all">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-indigo-400 text-xs font-bold uppercase tracking-wider">
                    <Briefcase size={12} /> Project Manager
                </div>
                {hasMore && (
                    <button onClick={() => setExpanded(!expanded)} className="text-indigo-400 hover:text-indigo-300 text-xs flex items-center gap-1">
                        {expanded ? 'Show Less' : 'Read More'} {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                )}
            </div>
            <div className="text-sm leading-relaxed">
                {expanded ? (
                    <MessageRenderer content={content} isNested={true} />
                ) : (
                    <p><FormatInline text={previewText} />{hasMore && "..."}</p>
                )}
            </div>
        </div>
    );
}

function MessageRenderer({ content, isNested = false }) {
    if (!isNested && (content.startsWith('[PM RESPONSE]') || content.includes('PM RESPONSE:'))) {
        const cleanContent = content.replace(/\[PM RESPONSE\]:?|PM RESPONSE:/gi, '').trim();
        return <CollapsiblePM content={cleanContent} />;
    }

    const blocks = content.split(/(```(?:lua|luau|bash|json)?[\s\S]*?```)/g);

    return (
        <div className="space-y-2 text-sm md:text-base">
            {blocks.map((block, index) => {
                if (block.startsWith('```')) {
                    const firstLine = block.split('\n')[0];
                    const language = firstLine.replace('```', '').trim() || 'Script';
                    const code = block.replace(/^```.*\n?/, '').replace(/```$/, '');
                    return <CollapsibleCode key={index} code={code} language={language} />;
                }

                const lines = block.split('\n');
                return (
                    <div key={index} className="whitespace-pre-wrap leading-relaxed">
                        {lines.map((line, i) => {
                            const trimmed = line.trim();
                            if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
                                return <div key={i} className="flex items-start gap-2 ml-2 my-1"><span className="text-blue-500 mt-1.5">•</span><span className="text-slate-300"><FormatInline text={trimmed.substring(2)} /></span></div>;
                            }
                            if (/^\d+\.\s/.test(trimmed)) {
                                const [num, ...rest] = trimmed.split('.');
                                return <div key={i} className="flex items-start gap-2 ml-2 my-1"><span className="text-blue-500 font-mono font-bold">{num}.</span><span className="text-slate-300"><FormatInline text={rest.join('.').trim()} /></span></div>;
                            }
                            if (!trimmed) return <div key={i} className="h-2" />;
                            return <div key={i}><FormatInline text={line} /></div>;
                        })}
                    </div>
                );
            })}
        </div>
    );
}

function CollapsibleProcess({ logs }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="flex justify-start w-full max-w-lg animate-in fade-in slide-in-from-bottom-2 mb-4">
      <div className="w-full bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-lg transition-all duration-300">
        <div onClick={() => setIsOpen(!isOpen)} className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition-colors">
          <div className="flex items-center gap-3"><Loader2 size={18} className="animate-spin text-blue-400" /><span className="font-semibold text-sm text-white">Working...</span></div>
          <button className="text-slate-400">{isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</button>
        </div>
        {isOpen && <div className="bg-slate-950/50 p-4 border-t border-slate-800 max-h-48 overflow-y-auto font-mono text-xs">{logs.map((log, index) => (<div key={index} className="flex items-start gap-3 mb-1 last:mb-0"><span className="text-slate-600 select-none">{`>`}</span><span className={index === logs.length - 1 ? 'text-blue-300' : 'text-slate-400'}>{log}</span></div>))}</div>}
      </div>
    </div>
  );
}

function NavIcon({ icon, active, onClick, label }) {
    return <button onClick={onClick} className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 w-full text-left font-medium ${active ? 'bg-slate-800 text-blue-400' : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'}`}>{icon}<span>{label}</span></button>;
}

function CredentialCopyBox({ value, isRolling }) {
    const [copied, setCopied] = useState(false);
    const displayValue = (value && !isRolling) ? (value.length > 8 ? value.slice(0, 8) + "..." : value) : (isRolling ? "Generating..." : "...");
    const handleCopy = () => { if (value && !isRolling) { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); } };
    return (
        <div className={`flex items-center gap-2 bg-slate-950 p-3 rounded-lg border transition-all duration-300 mt-2 ${isRolling ? 'border-blue-500/50 opacity-50' : 'border-slate-800'}`}>
            <div className="flex-1 font-mono text-slate-300 text-sm truncate tracking-widest">{displayValue}</div>
            <button onClick={handleCopy} disabled={isRolling || !value} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors relative disabled:opacity-0" title="Copy Key">{copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}</button>
        </div>
    );
}
